<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { ScopeDescriptor } from "@/types/bbel-types";
import { filterScopeTree } from "./bbel-palette";
import BbelScopeTree from "./BbelScopeTree.vue";

/**
 * The BBEL smart picker (#5683): search-first. The global search box sits on top and looks across
 * EVERYTHING (scope tree at any depth + functions); while it has text the body is one flat ranked
 * result list, and the category row collapses away. With no search, the icon categories (icons
 * expand to their text on hover; the active one stays expanded) switch between the browsable scope
 * tree and the function chips. It never takes focus itself — inserts use `mousedown.prevent` so the
 * target field keeps its caret — except the search box, which the user focuses deliberately. Emits
 * the bare token to insert; the host routes it to the last-focused BBEL field.
 */
const props = defineProps<{
  scope: ScopeDescriptor;
  /** Resolved node paths of the chain at the target field's caret — each level auto-expands. */
  activePaths?: string[];
  /** False until a BBEL field has been focused — inserts have nowhere to land yet. */
  canInsert?: boolean;
  /** Overlay mode (narrow viewports) shows a close button; the docked column has none. */
  closable?: boolean;
}>();

const emit = defineEmits<{
  insert: [bare: string, cursorBackBy: number];
  close: [];
}>();

const categories = [
  { key: "data", label: "Data", icon: "bi-database" },
  { key: "functions", label: "Functions", icon: "bi-braces" },
] as const;
const activeCategory = ref<(typeof categories)[number]["key"]>("data");

const search = ref("");
const searching = computed(() => search.value.trim().length > 0);
const expanded = ref<Set<string>>(new Set());

// --- Global search: one query across the whole scope tree and the function catalogue. Data
// results keep their HIERARCHY — the tree is pruned to matches + ancestors, fully expanded, with
// the matched text marked. Functions (flat by nature) follow in a small section beneath. ---
const filteredRoots = computed(() => filterScopeTree(props.scope.roots, search.value));

const functionMatches = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return [];
  return props.scope.functions.filter(
    (f) =>
      f.name.toLowerCase().includes(q) ||
      f.signature.toLowerCase().includes(q) ||
      f.summary.toLowerCase().includes(q),
  );
});

const noMatches = computed(
  () => searching.value && filteredRoots.value.length === 0 && functionMatches.value.length === 0,
);

// Functions grouped into category buckets (alphabetical), each bucket keeping the API's name order.
const functionGroups = computed(() => {
  const byCategory = new Map<string, typeof props.scope.functions>();
  for (const fn of props.scope.functions)
    (byCategory.get(fn.category) ?? byCategory.set(fn.category, []).get(fn.category)!).push(fn);
  return [...byCategory.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, functions]) => ({ category, functions }));
});

function toggle(path: string) {
  const next = new Set(expanded.value);
  next.has(path) ? next.delete(path) : next.add(path);
  expanded.value = next;
}

// Typing follows into the tree: every level of the caret's chain expands (additive — the user's
// own toggles are kept), so `{{ services.service_1.output[0]. }}` shows that branch open in the
// picker. The paths arrive pre-resolved (chainAncestorPaths) so bracket hops expand like dots.
watch(
  () => props.activePaths,
  (paths) => {
    if (!paths || paths.length === 0) return;
    const next = new Set(expanded.value);
    for (const path of paths) next.add(path);
    expanded.value = next;
  },
  { immediate: true },
);

// A scope path inserts verbatim (caret after it); a function inserts `name()` with the caret between
// the parens (one char back from the end). Without a target field, inserts are inert.
const insertPath = (path: string) => props.canInsert && emit("insert", path, 0);
const insertFunction = (name: string) => props.canInsert && emit("insert", `${name}()`, 1);
</script>

<template>
  <div class="bbel-palette card" :class="{ 'bbel-palette--inert': !canInsert }">
    <!-- Search-first: the global search IS the header. Esc clears it (and stays in the box). -->
    <div class="card-header d-flex align-items-center gap-2">
      <input
        v-model="search"
        type="text"
        class="form-control form-control-sm"
        placeholder="Search…"
        @keydown.esc.stop="search = ''"
      />
      <i
        v-if="!canInsert"
        class="bi bi-info-circle text-secondary"
        data-bs-toggle="tooltip"
        title="Click into an expression field to insert."
      ></i>
      <button
        v-if="closable"
        type="button"
        class="btn-close"
        style="font-size: 0.6rem"
        @click="emit('close')"
        @mousedown.prevent
      ></button>
    </div>
    <div class="card-body p-2">
      <!-- Category icons (browse mode only — searching collapses them). Hover expands an icon to
           its text; the active category stays expanded, so the visible label is always present. -->
      <div v-if="!searching" class="d-flex gap-1 mb-2">
        <button
          v-for="cat in categories"
          :key="cat.key"
          type="button"
          class="bbel-cat"
          :class="{ active: activeCategory === cat.key }"
          :title="cat.label"
          @click="activeCategory = cat.key"
          @mousedown.prevent
        >
          <i class="bi" :class="cat.icon"></i>
          <span class="bbel-cat-label">{{ cat.label }}</span>
        </button>
      </div>

      <div class="bbel-palette-scroll">
        <!-- Global search: the pruned tree, fully expanded with matches marked — hierarchy stays
             visible. Matching functions follow beneath. -->
        <template v-if="searching">
          <BbelScopeTree
            :nodes="filteredRoots"
            :expanded="expanded"
            force-expand
            :highlight="search"
            @insert="insertPath"
            @toggle="toggle"
          />
          <template v-if="functionMatches.length">
            <div class="bbel-palette-fn-cat text-muted text-uppercase" :class="{ 'mt-2': filteredRoots.length }">
              Functions
            </div>
            <button
              v-for="fn in functionMatches"
              :key="fn.name"
              type="button"
              class="bbel-res btn btn-sm d-flex align-items-baseline w-100 text-start"
              :title="`${fn.signature}\n${fn.summary}`"
              @click="insertFunction(fn.name)"
              @mousedown.prevent
            >
              <span class="bbel-res-key">{{ fn.name }}()</span>
              <span class="bbel-res-crumb text-truncate">{{ fn.summary }}</span>
            </button>
          </template>
          <div v-if="noMatches" class="text-muted small fst-italic">No matches</div>
        </template>

        <!-- Browse mode: the active category. -->
        <template v-else-if="activeCategory === 'data'">
          <BbelScopeTree :nodes="scope.roots" :expanded="expanded" @insert="insertPath" @toggle="toggle" />
          <div v-if="scope.roots.length === 0" class="text-muted small fst-italic">No data available</div>
        </template>

        <template v-else>
          <div v-for="group in functionGroups" :key="group.category" class="bbel-palette-fn-group">
            <div class="bbel-palette-fn-cat text-muted text-uppercase">{{ group.category }}</div>
            <div class="d-flex flex-wrap gap-1">
              <button
                v-for="fn in group.functions"
                :key="fn.name"
                type="button"
                class="btn btn-sm btn-outline-secondary bbel-palette-fn"
                :title="`${fn.signature}\n${fn.summary}`"
                @click="insertFunction(fn.name)"
                @mousedown.prevent
              >
                {{ fn.name }}
              </button>
            </div>
          </div>
          <div v-if="functionGroups.length === 0" class="text-muted small fst-italic">No functions available</div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bbel-palette {
  width: 340px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0; /* let the tree scroll instead of growing the column */
}
.bbel-palette--inert .bbel-palette-scroll {
  opacity: 0.65;
}
.bbel-palette .card-header {
  --bs-card-cap-padding-y: 0.3rem;
  --bs-card-cap-padding-x: 0.5rem;
}
.bbel-palette .card-body {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.bbel-palette-scroll {
  overflow-y: auto;
  flex: 1 1 auto;
}
/* Category buttons: icon at rest, expanding to icon + text on hover; the active one stays
   expanded (its visible label keeps the no-icon-only-buttons rule satisfied). */
.bbel-cat {
  display: inline-flex;
  align-items: center;
  border: 0;
  border-radius: var(--bs-border-radius);
  padding: 0.2rem 0.45rem;
  background: transparent;
  color: var(--bs-secondary-color);
  font-size: 0.8rem;
}
.bbel-cat:hover {
  background: var(--bs-secondary-bg);
  color: var(--bs-body-color);
}
.bbel-cat.active {
  background: var(--bs-primary);
  color: #fff;
}
.bbel-cat-label {
  max-width: 0;
  overflow: hidden;
  white-space: nowrap;
  margin-left: 0;
  transition: max-width 0.2s ease, margin-left 0.2s ease;
}
.bbel-cat:hover .bbel-cat-label,
.bbel-cat.active .bbel-cat-label {
  max-width: 6rem;
  margin-left: 0.35rem;
}
/* Flat search-result rows. */
.bbel-res {
  padding: 0.1rem 0.25rem;
  border: 0;
  gap: 0.4rem;
  min-width: 0;
}
.bbel-res:hover {
  background: var(--bs-secondary-bg);
}
.bbel-res-key {
  font-family: var(--bs-font-monospace, monospace);
  font-size: 0.8rem;
  color: var(--bs-emphasis-color);
  white-space: nowrap;
}
.bbel-res-crumb {
  font-size: 0.72rem;
  color: var(--bs-secondary-color);
}
.bbel-palette-fn {
  font-family: var(--bs-font-monospace, monospace);
  font-size: 0.72rem;
  padding: 0.1rem 0.4rem;
}
.bbel-palette-fn-group + .bbel-palette-fn-group {
  margin-top: 0.5rem;
}
.bbel-palette-fn-cat {
  font-size: 0.62rem;
  letter-spacing: 0.04em;
  margin-bottom: 0.2rem;
}
</style>
